/**
 * <drop-countdown> — counts down to the next product drop.
 *
 * Two modes, set with data-mode:
 *   "date"   — a one-off moment, data-target="YYYY-MM-DDTHH:MM:SS"
 *   "weekly" — the same slot every week, data-weekday="0-6" (0 = Sunday) and
 *              data-time="HH:MM". When the slot passes it rolls on to next week
 *              rather than sitting at zero.
 *
 * Weekly times are UK wall-clock times. That matters: the UK is GMT for half the
 * year and BST for the other half, so a fixed UTC offset would be an hour out every
 * summer, and using the viewer's own clock would be wrong for anyone abroad. The next
 * slot is resolved against Europe/London and converted to a real instant.
 *
 * A custom element rather than a per-section script so the same logic serves the
 * homepage drop section and the mini timer on sold-out product pages, and so the
 * theme editor's section reloads start and stop it without any extra wiring.
 */

const TIMEZONE = 'Europe/London';

const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIMEZONE,
  hour12: false,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** The wall-clock reading a given instant shows in the UK. */
function zoneParts(date) {
  const out = {};
  for (const part of formatter.formatToParts(date)) out[part.type] = part.value;

  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    // en-GB reports midnight as 24; the date has already rolled over by then.
    hour: Number(out.hour) % 24,
    minute: Number(out.minute),
    second: Number(out.second),
    weekday: WEEKDAY_INDEX[out.weekday],
  };
}

/**
 * Turn a UK wall-clock reading into a UTC timestamp.
 *
 * Date.UTC treats the reading as though it were UTC; the difference between that and
 * what the same instant actually shows in London is the offset to take off. Measured
 * twice because the first guess can land on the far side of a clock change.
 */
function zonedToUtc(year, month, day, hour, minute) {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0);

  const offsetAt = (ms) => {
    const p = zoneParts(new Date(ms));
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ms;
  };

  return guess - offsetAt(guess - offsetAt(guess));
}

class DropCountdown extends HTMLElement {
  #interval = null;
  #targetTime = NaN;

  connectedCallback() {
    this.#targetTime = this.#computeTarget();

    const fields = this.#fields();
    if (Number.isNaN(this.#targetTime) || !fields) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#interval = null;
  }

  #fields() {
    const days = this.querySelector('[data-days]');
    const hours = this.querySelector('[data-hours]');
    const minutes = this.querySelector('[data-minutes]');
    const seconds = this.querySelector('[data-seconds]');
    if (!days || !hours || !minutes || !seconds) return null;
    return { days, hours, minutes, seconds };
  }

  #computeTarget() {
    if (this.dataset.mode === 'weekly') return this.#nextWeekly();

    const target = (this.dataset.target || '').trim();
    return target ? new Date(target).getTime() : NaN;
  }

  #nextWeekly() {
    const weekday = Number(this.dataset.weekday);
    const time = (this.dataset.time || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6 || !time) return NaN;

    const hour = Number(time[1]);
    const minute = Number(time[2]);
    if (hour > 23 || minute > 59) return NaN;

    const now = zoneParts(new Date());
    let daysAhead = (weekday - now.weekday + 7) % 7;

    // On the day but past the slot (or exactly on it): aim at next week's.
    const secondsNow = now.hour * 3600 + now.minute * 60 + now.second;
    if (daysAhead === 0 && secondsNow >= hour * 3600 + minute * 60) daysAhead = 7;

    return zonedToUtc(now.year, now.month, now.day + daysAhead, hour, minute);
  }

  #tick() {
    const fields = this.#fields();
    if (!fields) return;

    let diff = this.#targetTime - Date.now();

    if (diff <= 0 && this.dataset.mode === 'weekly') {
      this.#targetTime = this.#computeTarget();
      diff = this.#targetTime - Date.now();
    }

    if (diff <= 0) {
      for (const field of Object.values(fields)) field.textContent = '00';
      this.disconnectedCallback();
      return;
    }

    const pad = (n) => String(n).padStart(2, '0');
    fields.days.textContent = pad(Math.floor(diff / 86400000));
    fields.hours.textContent = pad(Math.floor((diff % 86400000) / 3600000));
    fields.minutes.textContent = pad(Math.floor((diff % 3600000) / 60000));
    fields.seconds.textContent = pad(Math.floor((diff % 60000) / 1000));
  }
}

if (!customElements.get('drop-countdown')) {
  customElements.define('drop-countdown', DropCountdown);
}
